import useDocumentTitle from "@/components/ui/common/document-title";
import React, { useState } from "react";
import RevenueReportFilters from "./shared/revenue-report-filters";
import RevenueChart from "./charts/revenue-chart";
import RevenueReportTable from "./table";
import { Card, CardContent } from "@/components/ui/card";
import { useGetTransactionItemsQuery } from "@/services/transactionApi";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { formatDate } from "@/lib/helper";

export default function RevenueReport() {
  useDocumentTitle("Revenue Report");

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

  // Filter type that supports both single values and arrays
  type FilterState = {
    start_date?: string;
    end_date?: string;
    item_type?: string;
    item_ids?: (string | number)[];
    facility_ids?: (string | number)[];
  };

  // Applied filters (what actually gets sent to the API)
  const [appliedFilters, setAppliedFilters] = useState<FilterState>({});

  // Pending filters (what user is selecting but hasn't applied yet)
  const [pendingFilters, setPendingFilters] = useState<FilterState>({});

  // Filter modal state
  const [openFilter, setOpenFilter] = useState(false);

  // Initial value for filters
  const initialValue = {
    limit: 10,
    offset: 0,
    sort_order: "desc",
    sort_key: "created_at",
  };

  // Build query for charts/summary (fetch more data for visualization)
  const buildSummaryQuery = () => {
    const params = new URLSearchParams();
    params.append("offset", "0");
    params.append("sort_order", "desc");
    params.append("sort_key", "created_at");
    params.append("load_summary", "true");

    // Add applied filters to query
    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value) {
        // Handle arrays (multiselect) or other single values
        if (Array.isArray(value)) {
          value.forEach((val) => {
            if (val) {
              params.append(key, String(val));
            }
          });
        } else {
          params.append(key, String(value));
        }
      }
    });

    return params.toString();
  };

  // API call for charts/summary
  const { data: summaryData, isLoading: summaryLoading } =
    useGetTransactionItemsQuery(
      { query: buildSummaryQuery(), org_id: orgId },
    );

  const chartData = React.useMemo(() => {
    return summaryData?.summary;
  }, [summaryData]);

  // Handle date range change (pending state only)
  const handleDateRange = (dates: {
    start_date: Date | undefined;
    end_date: Date | undefined;
  }) => {
    const formattedStartDate = dates.start_date
      ? formatDate(dates.start_date)
      : "";
    const formattedEndDate = dates.end_date ? formatDate(dates.end_date) : "";

    setPendingFilters((prev) => ({
      ...prev,
      start_date: formattedStartDate || undefined,
      end_date: formattedEndDate || undefined,
    }));
  };

  // Apply filters - this is called when user clicks "Apply Filters"
  const applyFilters = () => {
    setAppliedFilters(pendingFilters);
    setOpenFilter(false);
  };

  // Reset filters
  const resetFilters = () => {
    setPendingFilters({});
    setAppliedFilters({});
    setOpenFilter(false);
  };

  return (
    <div className="w-full p-5 space-y-6">
      {/* Shared Filters Component */}
      <RevenueReportFilters
        dateRange={{
          start_date: pendingFilters.start_date,
          end_date: pendingFilters.end_date,
        }}
        appliedDateRange={{
          start_date: appliedFilters.start_date,
          end_date: appliedFilters.end_date,
        }}
        onDateRangeChange={handleDateRange}
        onResetFilters={resetFilters}
        onApplyFilters={applyFilters}
        filterData={pendingFilters}
        setFilterData={setPendingFilters}
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
        initialValue={initialValue}
        showAdvancedFilters={true}
      />

      {/* Charts Section */}
      <div className="space-y-6">
        <RevenueChart 
          data={chartData} 
          isLoading={summaryLoading} 
          filters={appliedFilters}
        />

        {/* Table Section */}
        <Card>
          <CardContent className="pt-6">
            <RevenueReportTable 
              appliedFilters={appliedFilters}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
